import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Upload, X, ImagePlus, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { PROPERTY_TYPES, CONTACT_METHODS, SITE } from "@/data/site";
import { SERVICE_OPTIONS } from "@/data/services";

const TIME_WINDOWS = ["Morning (8am–12pm)", "Afternoon (12pm–4pm)", "Evening (4pm–7pm)", "Flexible"];
const MAX_FILE_MB = 10;
const OK_TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

const fieldCls =
  "bg-[#0f0f15] border-[#2a2a34] text-white placeholder:text-neutral-500 focus-visible:ring-amber-400/60";

export default function QuoteForm({ defaultService = "" }) {
  const today = new Date().toISOString().split("T")[0];
  const [photos, setPhotos] = useState([]); // {id, url, filename}
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null); // {id}

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      full_name: "",
      phone: "",
      email: "",
      service_address: "",
      city: "",
      zip_code: "",
      property_type: "",
      service_needed: defaultService,
      description: "",
      preferred_contact_method: "Phone",
      preferred_date: "",
      preferred_time: "",
    },
  });

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (!OK_TYPES.includes(file.type)) {
        toast.error(`${file.name}: unsupported type. Use JPG, PNG, WEBP or HEIC.`);
        continue;
      }
      if (file.size > MAX_FILE_MB * 1024 * 1024) {
        toast.error(`${file.name}: larger than ${MAX_FILE_MB}MB.`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/uploads", fd);
        setPhotos((p) => [...p, { id: data.id, url: fileUrl(data.id), filename: data.filename }]);
      } catch (err) {
        toast.error(formatApiError(err, `Could not upload ${file.name}`));
      }
    }
    setUploading(false);
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const onSubmit = async (values) => {
    try {
      const payload = { ...values, photo_ids: photos.map((p) => p.id) };
      if (!payload.preferred_date) delete payload.preferred_date;
      if (!payload.preferred_time) delete payload.preferred_time;
      const { data } = await api.post("/quotes", payload);
      setDone({ id: data.id });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiError(err, "Could not submit your request. Please try again."));
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-amber-400/30 bg-[#101017] p-8 text-center sm:p-12"
        data-testid="quote-success"
      >
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-400/15">
          <CheckCircle2 className="h-9 w-9 text-amber-400" />
        </div>
        <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white">Request received</h3>
        <p className="mx-auto mt-3 max-w-md text-neutral-400">
          Thank you. Your request has been sent to Kensmarc Electric. Marcos will review the details and reach
          out to discuss your project and next steps. This is a request — no appointment is confirmed until
          we contact you.
        </p>
        <p className="mt-4 text-sm text-neutral-500">
          Reference: <span className="font-mono text-amber-400">{done.id.slice(0, 8).toUpperCase()}</span>
        </p>
        <a
          href={SITE.phonePrimary.tel}
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-[#2e2e39] px-6 py-3 text-sm font-semibold text-white hover:border-amber-400/60"
          data-testid="quote-success-call"
        >
          <Phone className="h-4 w-4 text-amber-400" /> Prefer to talk now? Call {SITE.phonePrimary.display}
        </a>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="rounded-2xl border border-[#22222c] bg-[#101017] p-5 sm:p-8"
      data-testid="quote-form"
      noValidate
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full Name" error={errors.full_name} required>
          <Input
            {...register("full_name", { required: "Your name is required", minLength: { value: 2, message: "Enter your full name" } })}
            className={fieldCls}
            placeholder="Jane Doe"
            data-testid="quote-full-name"
          />
        </Field>

        <Field label="Phone Number" error={errors.phone} required>
          <Input
            type="tel"
            {...register("phone", {
              required: "Phone number is required",
              validate: (v) => (v.replace(/\D/g, "").length >= 10 ? true : "Enter a valid phone number"),
            })}
            className={fieldCls}
            placeholder="(626) 555-0123"
            data-testid="quote-phone"
          />
        </Field>

        <Field label="Email" error={errors.email} required>
          <Input
            type="email"
            {...register("email", {
              required: "Email is required",
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" },
            })}
            className={fieldCls}
            placeholder="you@email.com"
            data-testid="quote-email"
          />
        </Field>

        <Field label="Preferred Contact Method" error={errors.preferred_contact_method}>
          <Controller
            control={control}
            name="preferred_contact_method"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className={fieldCls} data-testid="quote-contact-method">
                  <SelectValue placeholder="How should we reach you?" />
                </SelectTrigger>
                <SelectContent>
                  {CONTACT_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>

        <Field label="Service Address" error={errors.service_address} required className="sm:col-span-2">
          <Input
            {...register("service_address", { required: "Service address is required" })}
            className={fieldCls}
            placeholder="Street address"
            data-testid="quote-address"
          />
        </Field>

        <Field label="City" error={errors.city} required>
          <Input {...register("city", { required: "City is required" })} className={fieldCls} placeholder="Azusa" data-testid="quote-city" />
        </Field>

        <Field label="ZIP Code" error={errors.zip_code} required>
          <Input
            inputMode="numeric"
            {...register("zip_code", {
              required: "ZIP code is required",
              pattern: { value: /^\d{5}(-\d{4})?$/, message: "Enter a valid ZIP code" },
            })}
            className={fieldCls}
            placeholder="91702"
            data-testid="quote-zip"
          />
        </Field>

        <Field label="Property Type" error={errors.property_type} required>
          <Controller
            control={control}
            name="property_type"
            rules={{ required: "Select a property type" }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className={fieldCls} data-testid="quote-property-type">
                  <SelectValue placeholder="Select property type" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((p) => (
                    <SelectItem key={p} value={p}>{p}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>

        <Field label="Service Needed" error={errors.service_needed} required>
          <Controller
            control={control}
            name="service_needed"
            rules={{ required: "Select the service you need" }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className={fieldCls} data-testid="quote-service">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>

        <Field label="Project / Problem Description" error={errors.description} required className="sm:col-span-2">
          <Textarea
            rows={5}
            {...register("description", {
              required: "Please describe your project",
              minLength: { value: 5, message: "Add a little more detail" },
            })}
            className={fieldCls}
            placeholder="Tell us what you need — the more detail, the better we can prepare your quote."
            data-testid="quote-description"
          />
        </Field>

        <Field label="Preferred Appointment Date (optional)" error={errors.preferred_date}>
          <Input type="date" min={today} {...register("preferred_date")} className={`${fieldCls} [color-scheme:dark]`} data-testid="quote-date" />
        </Field>

        <Field label="Preferred Time (optional)" error={errors.preferred_time}>
          <Controller
            control={control}
            name="preferred_time"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className={fieldCls} data-testid="quote-time">
                  <SelectValue placeholder="Select a time window" />
                </SelectTrigger>
                <SelectContent>
                  {TIME_WINDOWS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
      </div>

      {/* Photo upload */}
      <div className="mt-6">
        <Label className="mb-2 block text-sm font-medium text-neutral-200">
          Add job photos (optional)
        </Label>
        <div className="flex flex-wrap gap-3">
          {photos.map((p) => (
            <div key={p.id} className="relative h-20 w-20 overflow-hidden rounded-lg border border-[#2a2a34]">
              <img src={p.url} alt={p.filename || "Uploaded job photo"} className="h-full w-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(p.id)}
                className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/70 text-white"
                aria-label="Remove photo"
                data-testid={`quote-remove-photo-${p.id.slice(0, 6)}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
          <label
            className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-[#33333f] text-neutral-400 transition-colors hover:border-amber-400/60 hover:text-amber-400"
            data-testid="quote-upload-label"
          >
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
            <span className="text-[0.65rem]">{uploading ? "Uploading" : "Add"}</span>
            <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid="quote-upload-input" />
          </label>
        </div>
        <p className="mt-2 text-xs text-neutral-500">JPG, PNG, WEBP or HEIC · up to {MAX_FILE_MB}MB each.</p>
      </div>

      <button
        type="submit"
        disabled={isSubmitting || uploading}
        data-testid="quote-submit"
        className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-amber-400 py-4 text-base font-bold uppercase tracking-wide text-[#0A0A0C] transition-all hover:bg-amber-300 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
        {isSubmitting ? "Sending…" : "Submit Request"}
      </button>
      <p className="mt-3 text-center text-xs text-neutral-500">
        Submitting sends your details to Kensmarc Electric. No appointment is confirmed until we contact you.
      </p>
    </form>
  );
}

function Field({ label, error, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="mb-1.5 block text-sm font-medium text-neutral-200">
        {label} {required && <span className="text-amber-400">*</span>}
      </Label>
      {children}
      {error && (
        <p className="mt-1 text-xs text-red-400" role="alert" data-testid="field-error">
          {error.message}
        </p>
      )}
    </div>
  );
}
